import type { IOverviewLastSelectedState } from '../overview/models'
import type { IContract } from '../contract/models'
import type {
  IMSService,
  ITermBreakdown,
  TEnterableTerm,
} from '../msService/models'
import type { ITsdSupplier } from '../tsd/models'
import type { TProduct } from '../product/models'
import type { ITask } from '../task/models'
import type { ICall } from '../call/models'
import type {
  IActivityEmail,
  IAddressedEmailForPost,
  IEmailMessage,
} from '../email/models'
import type { IMeeting } from '../meeting/models'
import type { ISupplier } from '../supplier/models'
import type { ITeamMember } from '../teamMember/models'
import type { TContact } from '../contact/models'
import type {
  IGroupItemValue,
  ISelectItem,
  IEntitySelectItem,
  IEntitySelectItemWithOptionalAvatar,
  TEntityId,
  IEntityDataItem,
  TDisplayString,
  TOmitNamed,
} from './../models'
import type {
  IOpportunityDocument,
  IOpportunityMSDocument,
  IOpportunityMSServiceDocument,
} from './../document/models'
import type {
  IOpportunityNote,
  IOpportunityMSNote,
  IOpportunityMSServiceNote,
} from './../note/models'

// Opportunity Overview

export interface IOpportunityOverview {
  opportunity: IOpportunity
  order?: never
  opportunityMasterServices: IOpportunityMasterService[]
  opportunityTeamMembers: IOpportunityTeamMember[]
  opportunityContacts: IOpportunityContact[]
  opportunityRegistrations: IOpportunityRegistration[]
  opportunityDocuments: IOpportunityDocument[]
  opportunityNotes: IOpportunityNote[]
  contracts: IContract[]
  tasks: ITask[]
  calls: ICall[]
  emails: IActivityEmail[]
  meetings: IMeeting[]
  opportunities: IOpportunity[]
  lastSelectedState: IOpportunityLastSelectedState | undefined
  [key: string]: any
}

export type TAggregateTerm = number | '--' | 'Varies' | undefined

export interface IOpportunity extends IEntityDataItem {
  descr: string
  order?: IEntitySelectItem
  company: IEntitySelectItemWithOptionalAvatar
  opportunityType: ISelectItem[]
  suppliers?: IEntitySelectItem[]
  supplierDescr?: TDisplayString
  probability?: number
  term?: TAggregateTerm // from msServices
  defaultTerm?: TEnterableTerm
  anticipatedCloseDate: string
  anticipatedCloseDays?: IGroupItemValue
  funnelValueAmount?: number // computed locally
  recurs?: string
  paid?: string
  estimatedMrcAmount?: number
  estimatedOneTimeAmount?: number
  mrcAmount?: number
  nrcAmount?: number
  oneTimeAmount?: number
  tcvAmount?: number
  owner?: IEntitySelectItem
  assignedTo?: IEntitySelectItem
  defaultProject?: IEntitySelectItem
  salesRep?: IEntitySelectItem
  subAgent?: IEntitySelectItem
  leadSourceType?: ISelectItem
  leadSourceCampaign?: IEntitySelectItem
  leadSourceEvent?: IEntitySelectItem
  leadSourceContact?: IEntitySelectItem
  numMasterServices: number
  stage: IEntitySelectItem
  status: ISelectItem
  [key: string]: any
}

export interface IAnticipatedCloseDayTotals {
  estMrcPlus1x?: { [key: string]: any }
  funnelValue?: { [key: string]: any }
}

export interface IOpportunityGroup {
  title: string
  items: IOpportunity[]
  anticipatedCloseDaysTotals: IAnticipatedCloseDayTotals
}

export interface IOpportunityMasterService extends IEntityDataItem {
  descr: string
  opportunity: IEntitySelectItem
  orderMasterService?: IEntitySelectItem
  origin: ISelectItem
  companyMasterService?: IEntitySelectItem
  estimatedMrcAmount: number | null
  estimatedOneTimeAmount: number | null
  term?: TAggregateTerm // from msServices
  termBreakdown?: ITermBreakdown // set when the msServices' terms vary
  defaultTerm?: TEnterableTerm
  mrcAmount?: number
  nrcAmount?: number
  oneTimeAmount?: number
  recursPaidDescr?: TDisplayString
  numLocations: number
  numServices: number
  project?: IEntitySelectItem
  status: ISelectItem
  opportunityMSServices?: IOpportunityMSService[]
  opportunityMSContacts?: IOpportunityContact[]
  opportunityMSTeamMembers?: IOpportunityMSTeamMember[]
  opportunityMSDocuments?: IOpportunityMSDocument[]
  opportunityMSNotes?: IOpportunityMSNote[]
}

export interface IOpportunityMSService extends IMSService {
  orderMSService?: IEntitySelectItem
  type?: ISelectItem
  closeByDate?: string
  opportunityMSServiceDocuments?: IOpportunityMSServiceDocument[]
  opportunityMSServiceNotes?: IOpportunityMSServiceNote[]
}

// Still Omit, so its fields are `any`: TOmitNamed exposes type errors in the
// kit and msService builders.
export interface IInputOpportunityMSService
  extends Omit<IOpportunityMSService, 'location' | 'hrid'> {}

export interface IOpportunityMSServiceMultiLocation
  extends IInputOpportunityMSService {
  location: IEntitySelectItem[]
  supplierProductOptionalItemsToInclude: IEntitySelectItem[]
}

export interface IInputOpportunityMSServiceFromKit
  extends IInputOpportunityMSService {
  poductOptionalItemsToInclude: ISelectItem[]
  locations?: IEntitySelectItem[]
}

export interface IOpportunityMSServiceMultiLocationMultiSupplier
  extends IInputOpportunityMSService {
  location: IEntitySelectItem[]
  productGroup: IEntitySelectItem
  product: TProduct
  suppliers: IEntitySelectItem[]
  supplierProductOptionalItemsToInclude: IEntitySelectItem[]
  supplierToInclude: IEntitySelectItem
}

export interface IOpportunityMSServiceId {
  id: TEntityId
}

// Opportunity LastSelectedState

export interface IOpportunityLastSelectedState
  extends IOverviewLastSelectedState {
  opportunityId: TEntityId
  selectedMSId?: TEntityId | null
  selectedContractId?: TEntityId | null
}

// Opportunity Registration

// A registration is the opportunity's record of having registered the deal
// with a supplier (or a supplier through a TSD). Its email is an
// IEmailMessage on `registrationEmail` (types/email/models.ts) -- the same
// shape as a message on a case's thread -- rather than flat fields of its own.
//
// What the user composes is IOpportunityRegistrationForPost, below: the
// registration's fields plus the IAddressedEmailForPost to send. The overview
// returns this type, with the message that send produced.

export interface IOpportunityRegistrationBase extends IEntityDataItem {
  opportunity: IEntitySelectItem
  type?: ISelectItem
  supplierAndTsdDescr?: TDisplayString | null
  // The registration's description, and its email's Subject: an email posted
  // without a subject of its own is sent with this one.
  descr: string
  // The email this registration sent, delivery and all. Null when it sent
  // none: it was registered manually, outside Cynque, and this only records
  // it.
  //
  // Whether the email went is this message's `deliveryState`; `status` below
  // is only the supplier's answer (opportunityRegistrationStatii.ts).
  registrationEmail: IEmailMessage | null
  // What a registration made manually recorded -- the registration's details,
  // and whatever the supplier gave the rep (a confirmation number, a contact).
  // Null on one that sent an email: the email says all of that.
  registrationDetails: string | null
  // The day the supplier approved it, 'YYYY-MM-DD': set by the dialog when the
  // Status becomes Approved, cleared when it stops being. Null until then.
  approvedOn: string | null
  // Composed by the data object, for the card's columns: the email's date over
  // its time (On), and its To and cc as one string each. Empty when it sent
  // none.
  emailedDateTimeDescr?: TDisplayString
  emailedToDescr?: TDisplayString
  emailedCcDescr?: TDisplayString
  status: ISelectItem
}

export interface IOpportunityRegistration extends IOpportunityRegistrationBase {
  supplier: ISupplier
  // Set for a Supplier + TSD registration, absent for Supplier alone.
  tsdSupplier?: ITsdSupplier
}

/**
 * The body of a registration's create: the registration's own fields, plus the
 * email to send with it.
 *
 * `registrationEmail` is present unless the registration was made manually
 * (the dialog's "Registered manually (record only, no email)"), which carries
 * none and reads back with `registrationEmail: null`. Only a create sends: a
 * PATCH never carries one.
 *
 * Its `subject` may be omitted: the email then goes with the registration's
 * `descr`, which is what the dialog's Subject field edits.
 *
 * It is the registration itself, with the id the webapp gave it: the server
 * makes the hrid and returns id, hrid and version. Left out are the fields
 * only the server writes, and the data object's display fields.
 */
export interface IOpportunityRegistrationForPost
  extends TOmitNamed<
    IOpportunityRegistration,
    | 'hrid'
    | 'version'
    | 'createdAt'
    | 'modifiedAt'
    | 'canBeDeleted'
    | 'emailedDateTimeDescr'
    | 'emailedToDescr'
    | 'emailedCcDescr'
    | 'registrationEmail'
    | 'registrationDetails'
  > {
  type: ISelectItem
  registrationEmail?: IAddressedEmailForPost
  // A registration made manually only: see IOpportunityRegistrationBase.
  registrationDetails?: string
}

// Opportunity TeamMember

interface IOpportunityTeamMemberBase extends IEntityDataItem {
  label?: string
  teamMember: ITeamMember
  splitFraction: number | null
  isOwner?: boolean
  isAssignedTo?: boolean
  status: ISelectItem
  [key: string]: any
}

export interface IOpportunityTeamMember extends IOpportunityTeamMemberBase {
  opportunity: IEntitySelectItem
}

export interface IOpportunityMSTeamMember extends IOpportunityTeamMemberBase {
  opportunityMasterService: IEntitySelectItem
  // Note: isOwner and isAssigned currently not implemented
  // for opportunityMSTeamMember, but may be in the future.
}

// Opportunity Contact

interface IOpportunityContactBase extends IEntityDataItem {
  label?: string
  contact: TContact
  splitFraction: number | null
  status: ISelectItem
  [key: string]: any
}

export interface IOpportunityContact extends IOpportunityContactBase {
  opportunity: IEntitySelectItem
}

export interface IOpportunityMSContact extends IOpportunityContactBase {
  opportunityMasterService: IEntitySelectItem
}

// Opportunity Stage

export interface IOpportunityStage extends IEntityDataItem {
  descr: string
  label?: string
  seqNum: number
}

// Parent Object Interfaces

export interface IOpportunityParentObjectForCreate {
  entityName: 'opportunity'
  object: IEntitySelectItem
  [key: string]: any
}

export interface IOpportunityMasterServiceParentObjectForCreate {
  entityName: 'opportunityMasterService' | 'masterService'
  object: IEntitySelectItem
  [key: string]: any
}

export interface IOpportunityMSServiceParentObjectForCreate {
  entityName: 'opportunityMSService'
  object: IEntitySelectItem
}

export function isOpportunity(val: unknown): val is IOpportunity {
  return (val as IOpportunity).hasOwnProperty('opportunityType')
}

export function isOpportunityMasterServices(
  val: unknown
): val is IOpportunityMasterService[] {
  return (
    Array.isArray(val) &&
    val.length > 0 &&
    (val as IOpportunityMasterService[])[0].hasOwnProperty(
      'opportunityMSServices'
    )
  )
}

export function assertIsOpportunity(
  fn: string,
  val: any
): asserts val is IOpportunity {
  if (!isOpportunity(val)) {
    throw new Error(`${fn} val should be an IOpportunity, but it's not.`)
  }
}
